import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiKitchenCapacityWindow,
  ApiKitchenCapacityWindowCreate,
  ApiKitchenCapacityWindowUpdate,
  PaginatedResponse,
} from "@/types/api";

// Mirrors ``use-opening-hours.ts`` — same React Query / ky / sileo trio,
// same field-selection convention. The CRUD endpoints live under
// ``/api/kitchen-capacity-windows/`` and require ``X-Location-Id`` (the
// staff client injects it automatically from the auth store).
const KITCHEN_CAPACITY_FIELDS = [
  "id",
  "day_of_week",
  "start_time",
  "end_time",
  "max_pax",
] as const satisfies readonly (keyof ApiKitchenCapacityWindow)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useKitchenCapacityWindows() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["kitchen-capacity-windows", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: KITCHEN_CAPACITY_FIELDS.join(",") });
      const res = await api
        .get(`kitchen-capacity-windows/?${params}`)
        .json<PaginatedResponse<ApiKitchenCapacityWindow>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateKitchenCapacityWindow() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (data: ApiKitchenCapacityWindowCreate) =>
      api.post("kitchen-capacity-windows/", { json: data }).json<ApiKitchenCapacityWindow>(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
  });
}

export function useUpdateKitchenCapacityWindow() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiKitchenCapacityWindowUpdate }) =>
      api
        .patch(`kitchen-capacity-windows/${id}/`, { json: changes })
        .json<ApiKitchenCapacityWindow>(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
  });
}

export function useDeleteKitchenCapacityWindow() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`kitchen-capacity-windows/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({
        queryKey: ["kitchen-capacity-windows", organizationId, locationId],
      });
    },
  });
}
